"use client";

// Step 8.5 Half A0 commit 0.2 / AC406: wizard Step 1 - basics.
// 5 required fields matching signup-step-1.html mockup exactly.

import { useState } from "react";
import { useFormContext } from "react-hook-form";
import type { SignupFormValues } from "./SignupWizard";
import { fieldClass, labelClass, primaryButtonClass } from "./styles";
import { LocaleLink } from "@/components/i18n/LocaleLink";

export function SignupStep1({
  submitError,
  onContinue,
}: {
  submitError?: string | null;
  onContinue: () => void;
}) {
  const {
    register,
    formState: { errors },
    trigger,
  } = useFormContext<SignupFormValues>();
  const [showPassword, setShowPassword] = useState(false);

  async function handleContinue() {
    const ok = await trigger(
      ["businessName", "contactName", "email", "phone", "password"],
      { shouldFocus: true },
    );
    if (ok) onContinue();
  }

  return (
    <div data-testid="signup-step-1">
      <h1 className="mb-2 text-center text-2xl font-semibold text-zinc-900">
        Start your application
      </h1>
      <p className="mb-8 text-center text-sm text-zinc-500">
        Let&apos;s get to know you. This takes about 30 seconds.
      </p>

      {submitError ? (
        <p
          data-testid="signup-step-error"
          className="mb-6 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {submitError}
        </p>
      ) : null}

      <label className={labelClass}>
        Business name <span className="text-red-600">*</span>
        <input
          type="text"
          placeholder="Lily's Bloom Boutique"
          data-testid="signup-businessName"
          maxLength={100}
          {...register("businessName", { required: "Business name is required." })}
          className={fieldClass}
        />
        {errors.businessName ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.businessName.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Your full name <span className="text-red-600">*</span>
        <input
          type="text"
          placeholder="Amalia Rodriguez"
          data-testid="signup-contactName"
          maxLength={100}
          autoComplete="name"
          {...register("contactName", { required: "Your full name is required." })}
          className={fieldClass}
        />
        {errors.contactName ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.contactName.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Business email <span className="text-red-600">*</span>
        <input
          type="email"
          placeholder="amalia@lilysbloom.com"
          data-testid="signup-email"
          autoComplete="email"
          {...register("email", {
            required: "Email is required.",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address.",
            },
          })}
          className={fieldClass}
        />
        <span className="mt-1 text-xs text-zinc-500">
          We&apos;ll send order updates and approval here.
        </span>
        {errors.email ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.email.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Mobile phone <span className="text-red-600">*</span>
        <input
          type="tel"
          placeholder="(305) 555-1234"
          data-testid="signup-phone"
          autoComplete="tel"
          {...register("phone", {
            required: "Phone is required.",
            validate: (v) =>
              (v ?? "").replace(/\D/g, "").length >= 10 ||
              "Phone must include at least 10 digits.",
          })}
          className={fieldClass}
        />
        {errors.phone ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.phone.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Preferred email language
        <select
          data-testid="signup-preferredLanguage"
          {...register("preferredLanguage")}
          className={fieldClass}
        >
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
        <span className="mt-1 text-xs text-zinc-500">
          We&apos;ll send your welcome, approval, and order updates in this
          language.
        </span>
      </label>

      <label className={labelClass}>
        Password <span className="text-red-600">*</span>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="8+ chars: upper, lower, number, symbol"
            data-testid="signup-password"
            autoComplete="new-password"
            {...register("password", {
              required: "Password is required.",
              // URGENT signup fix (trap #3): match Cognito's password policy
              // CLIENT-side so a weak password fails HERE on step 1, not with a
              // server 400 after the user finishes all 3 steps (the late-bounce).
              validate: (v) => {
                const s = v ?? "";
                if (s.length < 8) return "Password must be at least 8 characters.";
                if (!/[a-z]/.test(s)) return "Add a lowercase letter.";
                if (!/[A-Z]/.test(s)) return "Add an uppercase letter.";
                if (!/[0-9]/.test(s)) return "Add a number.";
                if (!/[^A-Za-z0-9]/.test(s)) return "Add a symbol (e.g. ! ? @ #).";
                return true;
              },
            })}
            className={`${fieldClass} pr-16`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#77C1BB]"
          >
            {showPassword ? "HIDE" : "SHOW"}
          </button>
        </div>
        {errors.password ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.password.message as string}
          </span>
        ) : null}
      </label>

      <button
        type="button"
        data-testid="signup-step-1-continue"
        onClick={handleContinue}
        className={`${primaryButtonClass} mt-2 w-full`}
      >
        Continue →
      </button>

      <p className="mt-6 text-center text-sm text-zinc-500">
        Already a Magnaflor customer?{" "}
        <LocaleLink href="/sign-in" className="font-semibold text-[#77C1BB] hover:underline">
          Sign in
        </LocaleLink>
      </p>
    </div>
  );
}
